import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Plus, Trash2, Layers, AlertTriangle, CheckCircle2, Calculator, X, Loader2,
} from "lucide-react";
import { fmtCurrencyExact } from "@/lib/constants";
import { Field } from "@/pages/Companies";

const EMPTY = {
  name: "", sku: "", category: "material", fence_types: [], unit: "ea",
  unit_price: 0, supplier_id: "none", description: "",
  is_composite: false, composite_bom: [], composite_cost_override: null,
  needs_cost_review: false,
};
const UNITS = ["ea", "lm", "sqm", "hr", "day", "kg", "m3"];

// -----------------------------------------------------------------------------
// BOM editor (shared with QuoteBuilder-opened editor). Renders only when the
// parent material has is_composite=true.
// -----------------------------------------------------------------------------
function BomEditor({ editing, setEditing, materials }) {
  const bom = useMemo(() => editing.composite_bom || [], [editing.composite_bom]);
  const override = editing.composite_cost_override;

  const componentIndex = useMemo(() => {
    const map = {};
    for (const m of materials || []) map[m.id] = m;
    return map;
  }, [materials]);

  const derivedCost = useMemo(() => {
    let t = 0;
    for (const row of bom) {
      const comp = componentIndex[row.material_id];
      if (!comp) continue;
      t += Number(row.qty_per_unit || 0) * Number(comp.unit_price || 0);
    }
    return t;
  }, [bom, componentIndex]);

  const effectiveCost = override != null && override !== ""
    ? Number(override)
    : bom.length > 0
      ? derivedCost
      : Number(editing.unit_price || 0);

  const addRow = () => {
    setEditing({
      ...editing,
      composite_bom: [...bom, { material_id: "", qty_per_unit: 1, notes: "" }],
    });
  };
  const updateRow = (idx, patch) => {
    const next = bom.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    setEditing({ ...editing, composite_bom: next });
  };
  const removeRow = (idx) => {
    setEditing({ ...editing, composite_bom: bom.filter((_, i) => i !== idx) });
  };

  // Only raw materials as BOM components — backend still guards against circular refs.
  const componentChoices = (materials || []).filter((m) => !m.is_composite && m.id !== editing.id);

  return (
    <div className="border border-cyan-200 bg-cyan-50/40 rounded-sm p-3 space-y-3" data-testid="bom-editor">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Layers size={14} className="text-cyan-700" />
          <div className="stamp-label text-cyan-800 text-[11px]">Bill of Materials · optional</div>
        </div>
        <div className="text-[10px] text-slate-500">
          Cost source: <span className="font-semibold text-slate-700">
            {override != null && override !== ""
              ? "Manual override"
              : bom.length > 0 ? "BOM aggregation" : "Fallback (unit price)"}
          </span>
        </div>
      </div>

      {bom.length === 0 && (
        <div className="text-[11px] text-slate-500 leading-relaxed bg-white/60 p-2 rounded-sm border border-dashed border-cyan-200">
          BOMs are optional. Without one, the composite falls back to the unit price below.
          Add components to derive the true cost, or pin a manual override to lock it.
        </div>
      )}

      {bom.map((row, idx) => {
        const comp = componentIndex[row.material_id];
        const lineCost = comp ? Number(row.qty_per_unit || 0) * Number(comp.unit_price || 0) : 0;
        return (
          <div key={idx} className="grid grid-cols-12 gap-2 items-start bg-white p-2 rounded-sm border border-slate-200" data-testid={`bom-row-${idx}`}>
            <div className="col-span-12 sm:col-span-6">
              <label className="stamp-label text-[9px] text-slate-500">Component</label>
              <Select value={row.material_id || undefined} onValueChange={(v) => {
                // Iter-67 bug fix: default qty_mode="pack" for raws with
                // pack_size so composites compute per-LM correctly (see
                // Materials.jsx for the same fix).
                const raw = (componentChoices || []).find((m) => m.id === v);
                const shouldDefaultPack = !!(raw && raw.pack_size && Number(raw.pack_size) > 0);
                updateRow(idx, {
                  material_id: v,
                  ...(row.qty_mode == null && shouldDefaultPack ? { qty_mode: "pack" } : {}),
                });
              }}>
                <SelectTrigger data-testid={`bom-material-${idx}`} className="rounded-sm h-9"><SelectValue placeholder="Pick a raw material..." /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {componentChoices.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      <span className="text-xs">{m.name}</span>
                      <span className="text-[10px] text-slate-400 ml-2">${Number(m.unit_price || 0).toFixed(2)}/{m.unit}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-5 sm:col-span-2">
              <label className="stamp-label text-[9px] text-slate-500">Qty / unit</label>
              <Input
                data-testid={`bom-qty-${idx}`}
                type="number"
                step="0.01"
                value={row.qty_per_unit}
                onChange={(e) => updateRow(idx, { qty_per_unit: e.target.value === "" ? 0 : Number(e.target.value) })}
                className="rounded-sm h-9 font-mono text-sm"
              />
            </div>
            <div className="col-span-5 sm:col-span-3 text-right">
              <label className="stamp-label text-[9px] text-slate-500 block">Line cost</label>
              <div className="h-9 flex items-center justify-end font-mono text-sm text-slate-900">
                {comp ? fmtCurrencyExact(lineCost) : <span className="text-[10px] text-slate-400">pick component</span>}
              </div>
            </div>
            <div className="col-span-2 sm:col-span-1 flex items-end justify-end">
              <Button
                size="sm"
                variant="ghost"
                className="h-8 w-8 p-0 text-rose-600 hover:bg-rose-50"
                onClick={() => removeRow(idx)}
                data-testid={`bom-remove-${idx}`}
              >
                <Trash2 size={13} />
              </Button>
            </div>
            {row.material_id && (
              <div className="col-span-12">
                <Input
                  placeholder="Notes (optional)"
                  value={row.notes || ""}
                  onChange={(e) => updateRow(idx, { notes: e.target.value })}
                  className="rounded-sm h-8 text-xs"
                />
              </div>
            )}
          </div>
        );
      })}

      <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
        <Button
          size="sm"
          variant="outline"
          onClick={addRow}
          className="rounded-sm h-9 border-cyan-300 text-cyan-700 hover:bg-cyan-50"
          data-testid="bom-add-row"
        >
          <Plus size={13} className="mr-1" /> Add component
        </Button>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="stamp-label text-[9px] text-slate-500">Derived from BOM</div>
            <div className="font-mono text-sm font-semibold text-slate-900" data-testid="bom-derived-cost">{fmtCurrencyExact(derivedCost)}</div>
          </div>
          <div className="text-right border-l border-slate-200 pl-4">
            <div className="stamp-label text-[9px] text-cyan-700">Effective cost / unit</div>
            <div className="font-mono text-base font-bold text-cyan-800" data-testid="bom-effective-cost">{fmtCurrencyExact(effectiveCost)}</div>
          </div>
        </div>
      </div>

      <div className="border-t border-cyan-200 pt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div>
          <label className="stamp-label text-[10px] text-slate-500">Manual override (locks the composite cost)</label>
          <div className="flex items-center gap-1.5">
            <Input
              data-testid="bom-override-input"
              type="number"
              step="0.01"
              placeholder="Leave blank to use BOM/fallback"
              value={override == null ? "" : override}
              onChange={(e) => setEditing({ ...editing, composite_cost_override: e.target.value === "" ? null : Number(e.target.value) })}
              className="rounded-sm h-9 font-mono text-sm flex-1"
            />
            {override != null && override !== "" && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setEditing({ ...editing, composite_cost_override: null })}
                className="h-9 w-9 p-0 text-slate-500"
                data-testid="bom-override-clear"
                title="Clear override"
              >
                <X size={13} />
              </Button>
            )}
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5">Overrides BOM aggregation when set.</div>
        </div>
        <div className="text-[11px] text-slate-600 flex items-start gap-1.5 bg-white/70 p-2 rounded-sm border border-cyan-100">
          <Calculator size={12} className="text-cyan-700 mt-0.5 shrink-0" />
          <span>Labour is a <b>separate line</b> on quotes — never baked into a composite's cost. GP margin is applied once at the quote total.</span>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// MaterialEditDialog — self-contained editor for a single material.
// Used by /materials list AND the "open material" link on Quote Builder V2
// section cards. All saves invalidate every ["materials"] query in the app
// so the picker + section costs pick up live changes without a reload.
// -----------------------------------------------------------------------------
export function MaterialEditDialog({ materialId, open, onClose, initialSeed, onSaved }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);

  // Load the specific material when editing an existing one.
  const { data: loaded } = useQuery({
    queryKey: ["material", materialId],
    queryFn: async () => (await api.get(`/materials/${materialId}`)).data,
    enabled: !!materialId && open,
  });

  const { data: suppliers = [] } = useQuery({
    queryKey: ["suppliers-all"],
    queryFn: async () => (await api.get("/suppliers")).data,
    enabled: open,
  });
  const { data: fenceTypes = [] } = useQuery({
    queryKey: ["fence-types"],
    queryFn: async () => (await api.get("/fence-types")).data,
    enabled: open,
  });
  const { data: materials = [] } = useQuery({
    queryKey: ["materials-all-for-bom"],
    queryFn: async () => (await api.get("/materials")).data,
    enabled: open,
  });

  // Seed the local editing state whenever the source material changes or the
  // dialog opens for a new material.
  useEffect(() => {
    if (!open) return;
    if (materialId) {
      if (loaded) {
        setEditing({
          ...EMPTY,
          ...loaded,
          supplier_id: loaded.supplier_id || "none",
          composite_bom: loaded.composite_bom || [],
        });
      }
    } else {
      setEditing({ ...EMPTY, ...(initialSeed || {}) });
    }
  }, [open, materialId, loaded, initialSeed]);

  const save = useMutation({
    mutationFn: async (body) => {
      const payload = {
        ...body,
        supplier_id: body.supplier_id === "none" ? null : body.supplier_id,
        unit_price: parseFloat(body.unit_price) || 0,
      };
      if (!payload.is_composite) {
        payload.composite_bom = [];
        payload.composite_cost_override = null;
      } else {
        payload.composite_bom = (payload.composite_bom || [])
          .filter((r) => r && r.material_id && Number(r.qty_per_unit) > 0)
          .map((r) => ({ material_id: r.material_id, qty_per_unit: Number(r.qty_per_unit), notes: r.notes || null }));
        if (payload.composite_cost_override === "" || payload.composite_cost_override === undefined) {
          payload.composite_cost_override = null;
        }
      }
      if (body.id) return (await api.patch(`/materials/${body.id}`, payload)).data;
      return (await api.post("/materials", payload)).data;
    },
    onSuccess: (data) => {
      // Invalidate EVERY materials query in the app so both /materials and
      // the QuoteBuilder picker re-fetch the fresh cost/BOM.
      qc.invalidateQueries({ predicate: (q) => q.queryKey?.[0]?.toString().startsWith("materials") });
      qc.invalidateQueries({ queryKey: ["material", data?.id] });
      toast.success("Material saved — quote picker updated.");
      onSaved?.(data);
      onClose?.();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not save material"),
  });

  const markReviewed = useMutation({
    mutationFn: async (id) => (await api.post(`/materials/${id}/mark-cost-reviewed`)).data,
    onSuccess: (data) => {
      qc.invalidateQueries({ predicate: (q) => q.queryKey?.[0]?.toString().startsWith("materials") });
      toast.success("Cost marked as reviewed");
      setEditing((e) => (e ? { ...e, needs_cost_review: false, last_cost_check: data?.last_cost_check } : e));
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not mark reviewed"),
  });

  const toggleFenceType = (ft) => {
    if (!editing) return;
    const has = (editing.fence_types || []).includes(ft);
    setEditing({ ...editing, fence_types: has ? editing.fence_types.filter((t) => t !== ft) : [...(editing.fence_types || []), ft] });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="rounded-sm sm:max-w-2xl" data-testid="material-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2 flex-wrap">
            {materialId ? "Edit material" : "Add material"}
            {editing?.is_composite && <Badge variant="outline" className="rounded-sm text-[10px] border-cyan-300 bg-cyan-50 text-cyan-700">BUNDLE</Badge>}
            {editing?.needs_cost_review && <Badge variant="outline" className="rounded-sm text-[10px] border-amber-300 bg-amber-50 text-amber-700">NEEDS REVIEW</Badge>}
          </DialogTitle>
        </DialogHeader>

        {!editing && (
          <div className="py-12 flex items-center justify-center text-slate-400 text-sm">
            <Loader2 size={16} className="animate-spin mr-2" /> Loading material...
          </div>
        )}

        {editing && (
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Name" required><Input data-testid="material-name-input" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></Field>
              <Field label="SKU"><Input value={editing.sku || ""} onChange={(e) => setEditing({ ...editing, sku: e.target.value })} /></Field>
              <Field label="Category">
                <Select value={editing.category} onValueChange={(v) => setEditing({ ...editing, category: v })}>
                  <SelectTrigger data-testid="material-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="material">Material</SelectItem>
                    <SelectItem value="labour">Labour</SelectItem>
                    <SelectItem value="incidental">Incidental</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Unit">
                <Select value={editing.unit} onValueChange={(v) => setEditing({ ...editing, unit: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label={editing.is_composite ? "Fallback cost / unit (AUD)" : "Cost / unit (AUD)"} required>
                <Input data-testid="material-price-input" type="number" step="0.01" value={editing.unit_price} onChange={(e) => setEditing({ ...editing, unit_price: e.target.value })} />
              </Field>
              <Field label="Supplier">
                <Select value={editing.supplier_id || "none"} onValueChange={(v) => setEditing({ ...editing, supplier_id: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>

            <div className="flex items-start justify-between gap-3 border border-slate-200 rounded-sm p-3 bg-slate-50/50">
              <div className="flex items-start gap-2.5 min-w-0">
                <Layers size={16} className={`mt-0.5 ${editing.is_composite ? "text-cyan-700" : "text-slate-400"}`} />
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900">Composite bundle</div>
                  <div className="text-[11px] text-slate-500 leading-tight">
                    Turn on for ready-to-quote fence bundles (e.g. "Colorbond 1.8m LM"). Add optional BOM to derive cost from components.
                  </div>
                </div>
              </div>
              <Switch
                data-testid="is-composite-toggle"
                checked={!!editing.is_composite}
                onCheckedChange={(v) => setEditing({ ...editing, is_composite: v })}
              />
            </div>

            {editing.is_composite && (
              <BomEditor editing={editing} setEditing={setEditing} materials={materials} />
            )}

            {editing.needs_cost_review && editing.id && (
              <div className="border border-amber-300 bg-amber-50 rounded-sm p-3 flex items-center justify-between gap-2" data-testid="review-panel">
                <div className="flex items-start gap-2 min-w-0">
                  <AlertTriangle size={14} className="text-amber-700 mt-0.5 shrink-0" />
                  <div className="text-[11px] text-amber-900 leading-relaxed">
                    This composite's price came from the retired Pricing DB and is an old <b>sell rate</b>, not a validated cost. Populate the BOM or set a manual override, then mark reviewed.
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => markReviewed.mutate(editing.id)}
                  disabled={markReviewed.isPending}
                  className="rounded-sm h-8 border-emerald-400 text-emerald-800 hover:bg-emerald-50 shrink-0"
                  data-testid="mark-reviewed-dialog"
                >
                  <CheckCircle2 size={13} className="mr-1" /> Mark reviewed
                </Button>
              </div>
            )}

            <Field label="Applicable fence types">
              <div className="grid grid-cols-3 gap-1.5">
                {fenceTypes.map((ft) => {
                  const on = (editing.fence_types || []).includes(ft.value);
                  return (
                    <button
                      key={ft.value}
                      type="button"
                      onClick={() => toggleFenceType(ft.value)}
                      data-testid={`fence-type-${ft.value}`}
                      className={`px-2.5 py-1.5 text-xs border rounded-sm text-left transition-colors ${on ? "bg-cyan-600 text-white border-cyan-600" : "bg-white border-slate-200 hover:border-slate-400 text-slate-700"}`}
                    >
                      {ft.label}
                    </button>
                  );
                })}
              </div>
            </Field>
            <Field label="Description"><Textarea rows={2} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></Field>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button
            data-testid="save-material-btn"
            onClick={() => editing && save.mutate(editing)}
            disabled={!editing || save.isPending}
            className="rounded-sm bg-cyan-600 hover:bg-cyan-700 text-white"
          >
            {save.isPending ? <Loader2 size={13} className="animate-spin mr-1.5" /> : null}
            Save material
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
